import { createApi } from '@reduxjs/toolkit/query/react';
import { salaryBaseQuery } from '../../lib/axiosBaseQuery';

export const salaryPaymentsApi = createApi({
  reducerPath: 'salaryPaymentsApi',
  baseQuery: salaryBaseQuery,
  tagTypes: ['SalaryPayments', 'SalaryPaymentDetails', 'SalaryPFAs', 'SalaryEmployerInfo', 'SalarySchedule'],
  endpoints: (builder) => ({
    getSalaryPaymentHistory: builder.query<any, { year: string; month: string } | void>({
      query: (params) => ({
        url: 'Payment/history',
        method: 'GET',
        params: params ? { Year: params.year, Month: params.month } : undefined,
      }),
      providesTags: ['SalaryPayments'],
    }),
    getSalaryPFABeneficiaries: builder.query<any, string>({
      query: (id) => ({
        url: `Payment/${id}/pfa/items`,
        method: 'GET',
      }),
    }),
    getSalaryPaymentsItems: builder.query<any, string>({
      query: (id) => ({
        url: `Payment/${id}/items`,
        method: 'GET',
      }),
    }),
    createSalarySchedule: builder.mutation<any, any>({
      query: (data) => ({
        url: 'Payment/schedule/create',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['SalaryPayments'],
    }),
    removeSalaryItemFromSchedule: builder.mutation<any, { scheduleId: string; beneficiaryId: string }>({
      query: (data) => ({
        url: 'Payment/schedule/items/remove',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['SalarySchedule'],
    }),
    addSalaryItemToSchedule: builder.mutation<any, any>({
      query: (data) => ({
        url: 'Payment/schedule/items/add',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['SalarySchedule'],
    }),
    getSalaryPFAList: builder.query<any, void>({
      query: () => ({
        url: 'Payment/pfa/list',
        method: 'GET',
      }),
      providesTags: ['SalaryPFAs'],
    }),
    getSalaryEmployerInfo: builder.query<any, string>({
      query: (employerCode) => ({
        url: `Payment/employer/${employerCode}/info`,
        method: 'GET',
      }),
      providesTags: (result, error, employerCode) => [{ type: 'SalaryEmployerInfo', id: employerCode }],
    }),
    getSalaryPFAInfo: builder.query<any, string>({
      query: (pfaCode) => ({
        url: `Payment/pfainfo/${pfaCode}`,
        method: 'GET',
      }),
    }),
    getSalarySchedule: builder.query<any, string>({
      query: (id) => ({
        url: `Payment/schedule/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'SalarySchedule', id }],
    }),
    confirmSalarySchedule: builder.mutation<any, string>({
      query: (scheduleId) => ({
        url: `Payment/confirm/${scheduleId}`,
        method: 'GET',
      }),
      invalidatesTags: ['SalarySchedule', 'SalaryPayments'],
    }),
    validateSalaryPaymentOTP: builder.mutation<any, any>({
      query: (data) => ({
        url: 'Payment/validate',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['SalarySchedule', 'SalaryPayments'],
    }),
    validateSalaryPayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `Payment/schedule/validate/${id}`,
        method: 'GET',
      }),
      invalidatesTags: ['SalarySchedule', 'SalaryPayments'],
    }),
    reValidateSalaryCardPayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `Payment/schedule/${id}/verify/card-payment`,
        method: 'GET',
      }),
      invalidatesTags: ['SalarySchedule', 'SalaryPayments'],
    }),
    updateSalaryScheduleBeneficiary: builder.mutation<any, any>({
      query: (data) => ({
        url: 'Payment/schedule/update/item',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['SalarySchedule'],
    }),
  }),
});

export const {
  useGetSalaryPaymentHistoryQuery,
  useGetSalaryPFABeneficiariesQuery,
  useGetSalaryPaymentsItemsQuery,
  useCreateSalaryScheduleMutation,
  useRemoveSalaryItemFromScheduleMutation,
  useAddSalaryItemToScheduleMutation,
  useGetSalaryPFAListQuery,
  useGetSalaryEmployerInfoQuery,
  useGetSalaryPFAInfoQuery,
  useGetSalaryScheduleQuery,
  useConfirmSalaryScheduleMutation,
  useValidateSalaryPaymentOTPMutation,
  useValidateSalaryPaymentMutation,
  useReValidateSalaryCardPaymentMutation,
  useUpdateSalaryScheduleBeneficiaryMutation,
} = salaryPaymentsApi;
